// Канонические ролевые проверки для всех компонентов — глобальный mixin
// (main.js: Vue.mixin), по образцу setErrorMixin.
//
// Зачем mixin, а не mapGetters в каждом файле: проверки роли размазаны по 60
// файлам (370 штук), почти у всех уже есть свой computed со своей формой
// mapGetters — точечная правка означала бы 60 лишних ханков и 60 шансов
// сломать. Плюс сам `user_role` скопирован 70 раз одним и тем же блоком.
//
// Локальное определение в компоненте перекрывает mixin (Vue 2 отдаёт
// приоритет опциям компонента), поэтому 70 дублей можно снимать постепенно,
// а не одной волной.
//
// $store проверяется явно, без mapGetters: спеки монтируют компоненты и без
// стора, а глобальный mixin применяется ко всем из них.
import {
  STAFF_ROLES,
  INTERNAL_ROLES,
  MANAGER_PLUS_ROLES,
  ADMIN_PLUS_ROLES,
} from "@/constants/roles";

function role(vm) {
  const user = vm.$store && vm.$store.getters.getCurrentUser;
  return user ? user.role : "";
}

export default {
  computed: {
    // Форма 1-в-1 как у 70 локальных копий, вплоть до краевого случая:
    // разлогиненный getCurrentUser — это {} (мутация unsetCurrentUser), а не
    // null, поэтому ветка с "" не срабатывает и наружу уходит undefined.
    // Копии ведут себя так же; isStaff при этом корректно false.
    user_role() {
      return role(this);
    },
    // Сотрудник компании. См. STAFF_ROLES — это allowlist, зеркало бэкенда.
    isStaff() {
      return STAFF_ROLES.includes(role(this));
    },
    // Сотрудники + guest — «изнутри компании». Для смотровых мест (навигация,
    // колонки таблиц): guest видит всё, кроме персональных данных, а внешние
    // agent и partner — только свою узкую область.
    isInternal() {
      return INTERNAL_ROLES.includes(role(this));
    },
    // Управление компанией: настройки, реквизиты, шаблоны. Без рядового user.
    isManagerPlus() {
      return MANAGER_PLUS_ROLES.includes(role(this));
    },
    // Владелец компании и админ, без manager'а.
    isAdminPlus() {
      return ADMIN_PLUS_ROLES.includes(role(this));
    },
    // Внешний агент. Геттера не было вовсе, хотя isPartner/isGuest есть, —
    // поэтому проверки на agent писались строкой по месту и разъезжались.
    isAgent() {
      return role(this) === "agent";
    },
    isPartner() {
      return role(this) === "partner";
    },
    isGuest() {
      return role(this) === "guest";
    },
    // Одиночные роли — полный набор, зеркало ролевых геттеров стора.
    // Набор именно ПОЛНЫЙ намеренно: пока isSuperAdmin здесь не было,
    // замена `user_role != "superadmin"` на `!this.isSuperAdmin` в
    // Branches#unarchiveItem дала `!undefined` === true, и восстановление
    // филиала отбивалось no_access у самого суперадмина. Ошибка тихая:
    // ни варнинга, ни падения. Полнота набора закрыта тестом
    // «mixin отдаёт все ролевые геттеры стора».
    isSuperAdmin() {
      return role(this) === "superadmin";
    },
    isAdmin() {
      return role(this) === "admin";
    },
    isManager() {
      return role(this) === "manager";
    },
  },
  methods: {
    // Гвард действия для не-сотрудника: показывает тот же no_access-тост и
    // возвращает false, чтобы вызывающий просто вышел.
    //
    // Форма для НОВОГО кода:
    //
    //   async deleteItem(item) {
    //     if (!this.requireStaff()) return;
    //     …
    //   }
    //
    // Существующие 202 места намеренно НЕ переписаны: там идиома
    // `if (this.isStaff) { …тело… } else { $notify(no_access) }` осталась
    // как была. Разворачивать тело каждой функции значило смешать в одном
    // диффе механическую замену условия и перестройку 202 тел — такой дифф
    // не ревьюится. Переход на requireStaff() идёт постепенно: на сейчас
    // вызовов 10 (Transactions, TopupDialog, CarCard) — их гварды и так
    // переписывались по другим причинам.
    //
    // Для агента есть исключения по возможностям компании (agent_setting:
    // can_edit_booking, can_take_money) — там нужен не requireStaff, а явная
    // форма `isStaff || (isAgent && flag)`, см. BookingCard#editBooking.
    requireStaff() {
      if (this.isStaff) return true;
      this.$notify(this.$t("errors.no_access"), "", "warning");
      return false;
    },

    // Возможность агента, выданная компанией: agent_setting.can_edit_booking,
    // can_take_money, access_to_account. Для не-агента всегда false — форма
    // рассчитана на связку `isStaff || agentCan("...")`.
    //
    // Права агента живут в настройках, а не в роли: одна и та же роль в разных
    // компаниях настроена по-разному, поэтому такие места нельзя схлопывать в
    // isStaff. Прецедент — BookingCard#editBooking и #paymentStart.
    agentCan(capability) {
      if (!this.isAgent) return false;
      const user = this.$store && this.$store.getters.getCurrentUser;
      const settings = user && user.agent_setting;
      return !!(settings && settings[capability] === true);
    },
  },
};
